import { CommandBar, Spinner, SpinnerSize } from 'office-ui-fabric-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { SORTING_KEYS } from '../../../constants/sortingKeys';
import {
  getAllHearingsInitialize,
  getAllCityAreasInitialize,
} from '../../../features/actions/api';
import { isFetchingHearingsSelector } from '../../../features/apiFeatures/hearingSlice';
import {
  allCityAreaWithNumberOfHearingsSelector,
  isFetchingCityAreasSelector,
  numberOfHearingsOnCityAreaByCityAreaIdSelector,
  cityAreaByIdSelector,
} from '../../../features/apiFeatures/cityAreaSlice';
import { getAttr } from '../../../features/helpers/apiHelpers';
import {
  createCityArea,
  deleteCityArea,
  isCreatingCityAreaSelector,
  isPanelOpenSelector,
  isUpdatingCityAreaSelector,
  selectedCityAreaIdSelector,
  setIsPanelOpen,
  setSelectedCityAreaId,
  updateCityArea,
} from '../../../features/pageFeatures/cityAreaAdministrationPageSlice';
import { comparators } from '../../../utilities/comparators';
import {
  filterAndSortingKeys,
  noSelectionKey,
} from '../../../utilities/filterAndSortingsUtil';
import { HeaderWithCommandBar } from './HeaderWithCommandBar';
import { CreateCityAreaPanel } from './cityAreaAdministrationPage/CreateCityAreaPanel';
import { getCommandBarItems } from './cityAreaAdministrationPage/getCommandBarItems';
import { mapCityArea } from './cityAreaAdministrationPage/mapCityAreaItem';
import { CityAreaList } from './cityAreaAdministrationPage/CityAreaList';

const filterCityAreas = (filter) => (cityArea) =>
  filter === noSelectionKey ||
  (getAttr(cityArea, 'name') &&
    getAttr(cityArea, 'name').toLowerCase().includes(filter.toLowerCase()));

const commandBarStyles = {
  root: {
    backgroundColor: '#FAFAFA',
    paddingRight: '0px',
    Width: 'auto',
  },
};

const CityAreaAdministrationPage = () => {
  const dispatch = useDispatch();
  const { t: translator } = useTranslation();

  useEffect(() => {
    return function cleanup() {
      dispatch(getAllHearingsInitialize());
      dispatch(getAllCityAreasInitialize());
    };
  }, [dispatch]);

  const cityAreas = useSelector(allCityAreaWithNumberOfHearingsSelector);
  const isFetchingCityAreas = useSelector(isFetchingCityAreasSelector);
  const isFetchingHearings = useSelector(isFetchingHearingsSelector);
  const selectedCityAreaId = useSelector(selectedCityAreaIdSelector);
  const isPanelOpen = useSelector(isPanelOpenSelector);
  const isCreatingCityArea = useSelector(isCreatingCityAreaSelector);
  const selectedCityArea = useSelector(
    cityAreaByIdSelector(selectedCityAreaId),
  );
  const isUpdatingCityArea = useSelector(isUpdatingCityAreaSelector);
  const selectedCityAreaNumberOfHearings = useSelector(
    numberOfHearingsOnCityAreaByCityAreaIdSelector(selectedCityAreaId),
  );
  const [titleFilter, setTitleFilter] = useState(null);
  const [showSearchBar, setShowSearchBar] = useState(false);
  const [cityAreaTitle, setCityAreaTitle] = useState('');
  const [filterAndSortingSelection, setFilterAndSortingSelection] = useState({
    [filterAndSortingKeys.sortingKey]: SORTING_KEYS.ALPHABETICAL,
    [filterAndSortingKeys.title]: noSelectionKey,
  });

  const onChangeCityAreaIsActive = (isActive) =>
    selectedCityAreaId &&
    dispatch(
      updateCityArea({
        cityAreaId: selectedCityAreaId,
        isActive: isActive,
      }),
    );
  const openPanel = () => {
    setCityAreaTitle('');
    dispatch(setIsPanelOpen(true));
  };
  const onPanelClose = () => {
    setCityAreaTitle();
    dispatch(setIsPanelOpen(false));
  };
  const onDeleteCityArea = () => {
    selectedCityAreaId && dispatch(deleteCityArea(selectedCityAreaId));
  };
  const onConfirmCreateCityArea = () => {
    dispatch(createCityArea(cityAreaTitle));
  };

  const filteredCityAreas = cityAreas.filter(
    filterCityAreas(filterAndSortingSelection[filterAndSortingKeys.title]),
  );

  const itemsToRender = filteredCityAreas.map(mapCityArea(translator));

  const sortedItemsToRender = !!comparators.editCityAreas[
    filterAndSortingSelection[filterAndSortingKeys.sortingKey]
  ]
    ? itemsToRender.sort(
        comparators.editCityAreas[
          filterAndSortingSelection[filterAndSortingKeys.sortingKey]
        ],
      )
    : itemsToRender;

  const onSetTitleFilter = (value) => {
    setTitleFilter(value);

    let newState = Object.assign({}, filterAndSortingSelection);
    newState[filterAndSortingKeys.title] = value || noSelectionKey;
    setFilterAndSortingSelection(newState);
  };

  const onSearchBarClick = () => {
    setShowSearchBar(!showSearchBar);
    if (showSearchBar) {
      onSetTitleFilter(null, null);
    }
  };

  const selectedCityAreaIsActive = selectedCityArea
    ? getAttr(selectedCityArea, 'isActive')
    : null;

  const topCommandBarItems = getCommandBarItems(
    translator,
    onChangeCityAreaIsActive,
    selectedCityAreaIsActive,
    selectedCityAreaId,
    onDeleteCityArea,
    isUpdatingCityArea,
    selectedCityAreaNumberOfHearings,
    openPanel,
  );

  const isFetchingData = isFetchingCityAreas || isFetchingHearings;

  return (
    <div className="admin-page">
      <div className="admin-page-commandBar">
        <CommandBar items={topCommandBarItems} styles={commandBarStyles} />
      </div>
      <div className="admin-page-container">
        <HeaderWithCommandBar
          header={translator('pages.adminCityAreas.header')}
          titleFilterText={translator('pages.adminCityAreas.filterText')}
          showTitleFilter={showSearchBar}
          setShowTitleFilter={onSearchBarClick}
          titleFilter={titleFilter}
          setTitleFilter={onSetTitleFilter}
          disableGrid={true}
        />
        {isFetchingData ? (
          <Spinner size={SpinnerSize.large} />
        ) : (
          <CityAreaList
            cityAreaItems={sortedItemsToRender}
            filterAndSortingSelection={filterAndSortingSelection}
            setFilterAndSortingSelection={setFilterAndSortingSelection}
            onCityAreaClick={(cityAreaId) =>
              dispatch(setSelectedCityAreaId(cityAreaId))
            }
            onCityAreaSelect={(cityAreaId) =>
              dispatch(setSelectedCityAreaId(cityAreaId))
            }
          />
        )}
        <CreateCityAreaPanel
          isOpen={isPanelOpen}
          onClose={onPanelClose}
          cityAreaTitle={cityAreaTitle}
          setCityAreaTitle={setCityAreaTitle}
          onConfirmCreateCityArea={onConfirmCreateCityArea}
          isCreatingCityArea={isCreatingCityArea}
        />
      </div>
    </div>
  );
};

export { CityAreaAdministrationPage };
